import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import SuccessModal, {
  useSuccess,
} from "../../../../../components/SuccessModal";
import Text from "../../../../../components/Text";
import ToggleSwitch from "../../../../../components/ToggleSwitch";
import ActionMenu from "../../../../../components/app/ActionMenu";
import UpsertProviderUserDrawer from "../../../../../components/app/admin/provider-groups/ProviderUserDrawer";
import UpsertStaffUserDrawer from "../../../../../components/app/admin/provider-groups/UpsertStaffUserDrawer";
import { STATE_LIST } from "../../../../../constants";
import { ProviderUserType } from "../../../../../constants/ProviderUserSchema";
import { StaffUser } from "../../../../../constants/staff-user-schema";
import { UserData } from "../../../../../dummy-data/user-data";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useStatusUsers } from "../../../../../queries/use-status-users";
import { useUpdateProviderUserStatus } from "../../../../../queries/use-update-provider-status";
import { useUpdateProviderUser } from "../../../../../queries/use-update-provider-user";
import { useUpdateUser } from "../../../../../queries/use-update-user";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import {
  DEFAULT_UUID_NAME_LIST,
  DEFAULT_VALUE_LABEL_LIST,
  applyDefaultColumnSettings,
  getUnNecessaryTransformData,
  getXtenantId,
  transformMultiSelectFrontEndVal,
} from "../../../../../utils/utils";
import { useArchiveState } from "../../../../../hooks/useArchiveState";
import { useUserArchive } from "../../../../../queries/use-archive-user";
import DeleteModal from "../../../../../components/app/DeleteModal";
import { useGetUserDetail } from "../../../../../queries/use-get-pertiCular-user";
import UserEmailCell from "../../../../../components/UserEmailCell";
import {
  RESEND_LINK_DISABLED_MSG,
  RESEND_LINK_LABEL,
  USER_TABLE_ROW_HEIGHT,
} from "./user-setting-constant";
import { useResendLink } from "../../../../../queries/use-resend-link";
import { UserCardData } from "../../../../../types/user-types";

type AllUserTableProps = {
  userList?: UserCardData[];
  searchKey: string;
  refetch: () => void;
};

const AllUserTable = ({ userList, searchKey, refetch }: AllUserTableProps) => {
  const { mutateAsync: statusChange } = useStatusUsers();
  const [selectedUserId, setSelectedUserId] = useState<string>();

  const { data: userDetail } = useGetUserDetail({
    userId: selectedUserId,
    xTENANTID: getXtenantId(),
    disable: !selectedUserId,
  });
  const {
    opened: openedStaffUserModal,
    open: openStaffUserModal,
    close: closeStaffUserModal,
  } = useDisclosure(false, { onClose: () => setSelectedUserId(undefined) });
  const {
    opened: openedProviderUserModal,
    open: openProviderUserModal,
    close: closeProviderUserModal,
  } = useDisclosure(false);
  const [openedResendLink, { open: openResendLink, close: closeResendLink }] =
    useSuccess();
  const { mutateAsync: updateStaffUser } = useUpdateUser();
  const { mutateAsync: updateProviderUser } = useUpdateProviderUser();
  const [archive, setArchive] = useState(false);
  const { mutateAsync: useArchiveUser } = useUserArchive();
  const { deleteDisc, archiveHandler } = useArchiveState(useArchiveUser);
  const [providerData, setProviderData] = useState<any>();
  const { mutateAsync: providerStatusUpdate } = useUpdateProviderUserStatus();
  const { mutateAsync: resendInvitationLink } = useResendLink();
  const queryClient = useQueryClient();

  const handleSendInviteLink = ({ email }: { email: string }) => {
    resendInvitationLink({
      linkType: "set",
      setPasswordRequest: {
        email: email,
      },
      xTENANTID: getXtenantId(),
    }).then(() => openResendLink());
  };

  // update staff user api call
  const editStaffUser = (values: StaffUser) => {
    const transFormData: any = getUnNecessaryTransformData(
      ["workLocations"],
      values
    );
    return updateStaffUser({
      user: transFormData,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      queryClient.invalidateQueries({
        queryKey: ["provider-group-users-list"],
      });

      return res;
    });
  };

  function editProviderUser(values: ProviderUserType) {
    return updateProviderUser({
      providerData: getUnNecessaryTransformData(
        ["licensedStates", "specialities"],
        values
      ) as any,
      xTENANTID: getXtenantId(),
    });
  }

  function toggleStatus<T>(value: boolean, id: T, role: string) {
    if (role === "PROVIDER") {
      return providerStatusUpdate({
        providerId: id?.toString() ?? "",
        status: value,
        xTENANTID: getXtenantId(),
      }).then((res) => {
        queryClient.invalidateQueries({
          queryKey: ["provider-group-users-list"],
        });

        return res;
      });
    }

    return statusChange({
      userId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      queryClient.invalidateQueries({
        queryKey: ["provider-group-users-list"],
      });
      return res;
    });
  }

  const COLUMNS = applyDefaultColumnSettings<UserData>([
    {
      field: "uuid",
      flex: 0.4,
      minWidth: 80,
      headerName: "User ID",
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Text isTabletext>
            {(row as any)?.uuid?.split("-")?.[0]?.toUpperCase()}
          </Text>
        );
      },
    },
    {
      field: "userInfo",
      flex: 0.8,
      minWidth: 200,
      disableColumnMenu: true,
      headerName: "User Name",
      // todo need to fix type
      renderCell: ({ row }: any) => {
        return (
          <Box

          // onClick={() => {
          //   row.roleType === "STAFF"
          //     ? navigate(`/app/provider-groups/users/staff/${row.uuid}`)
          //     : navigate(`/app/provider-groups/users/provider/${row.uuid}`);
          // }}
          >
            <Text>{`${row?.firstName} ${row.lastName}`}</Text>
            <Text variant="subtitle2">{row?.age}</Text>
          </Box>
        );
      },
      sortable: false,
    },
    {
      field: "role",
      flex: 0.5,
      minWidth: 130,
      headerName: "Role",
      sortable: false,
    },
    {
      field: "phone",
      flex: 0.6,
      minWidth: 140,
      headerName: "Contact",
      sortable: false,
    },
    {
      field: "email",
      flex: 0.9,
      minWidth: 240,
      headerName: "Email",
      renderCell: ({ row, value }) => (
        <UserEmailCell
          email={value}
          temporaryEmail={row?.temporaryEmail}
          isVerified={row?.emailVerified}
        />
      ),
      sortable: false,
    },
    {
      field: "lastLogin",
      flex: 0.7,
      minWidth: 150,
      headerName: "Last Sign In",
      sortable: false,
      renderCell: ({ ...params }) => {
        return (
          <Text isTabletext>
            {params.value ? formatDateTime(params.value) : "-"}
          </Text>
        );
      },
    },
    {
      field: "active",
      width: 150,
      // headerAlign: "center",
      // align: "center",
      headerName: "Status",
      renderCell: ({ row, value }) => (
        <ToggleSwitch
          id={row.uuid.toString()}
          checked={value}
          onConfirm={(v, id) => toggleStatus(v, id, row?.roleType)}
        />
      ),
      sortable: false,
    },
    {
      field: "Action",
      width: 90,
      // todo need to fix type
      renderCell: ({ row }: any) => {
        return (
          <ActionMenu
            options={[
              {
                value: "resendLink",
                label: RESEND_LINK_LABEL,
                isDisabled: Boolean(row?.emailVerified),
                hoverMsg: RESEND_LINK_DISABLED_MSG.USER_VERIFIED,
              },
              { value: "edit", label: "Edit" },
              {
                value: "archive",
                label: row?.archive ? "Unarchive" : "Archive",
                isDisabled: row?.active,
                hoverMsg: "Make status disable for archive",
              },
            ]}
            onActionClick={(v) => {
              if (v === "archive") {
                setArchive(row?.archive);
                deleteDisc.open(row?.uuid);
              } else if (v === "edit") {
                setProviderData(row);
                setSelectedUserId(row.uuid);

                if (row.roleType === "PROVIDER") {
                  openProviderUserModal();
                } else {
                  openStaffUserModal();
                }
              } else
                handleSendInviteLink({
                  email:
                    !row?.emailVerified && row?.temporaryEmail
                      ? row.temporaryEmail
                      : row?.email,
                });
            }}
          />
        );
      },
    },
  ]);

  return (
    <Box height={"100%"} width={"100%"}>
      <CustomDataGridV2
        rowHeight={USER_TABLE_ROW_HEIGHT}
        searchString={searchKey}
        getRowId={(row) => row.uuid}
        rows={userList ?? []}
        columns={COLUMNS}
        disableRowSelectionOnClick
        hideFooter={true}
      />
      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(!archive, id).then((res) => {
            refetch();
            return res;
          })
        }
        content={`You want to ${archive ? "unarchive" : "archive"} this User`}
      />

      <SuccessModal
        label="The invitation link has been sent successfully to your email."
        opened={openedResendLink}
        onClose={closeResendLink}
      />

      <UpsertStaffUserDrawer
        open={openedStaffUserModal}
        modalTitle="Edit User"
        onClose={closeStaffUserModal}
        initialValues={{
          ...userDetail?.data,
          workLocations: transformMultiSelectFrontEndVal(
            userDetail?.data?.workLocations
          ),
        }}
        onSubmit={editStaffUser}
      />
      <UpsertProviderUserDrawer
        open={openedProviderUserModal}
        title={"Edit Provider User"}
        onClose={closeProviderUserModal}
        onSubmit={editProviderUser}
        prefillValues={{
          ...(providerData ?? {}),
          acceptedInsurances: transformMultiSelectFrontEndVal(
            DEFAULT_VALUE_LABEL_LIST,
            providerData?.acceptedInsurances
          ),
          workLocations: transformMultiSelectFrontEndVal(
            DEFAULT_VALUE_LABEL_LIST,
            providerData?.workLocations
          ),
          licensedStates: transformMultiSelectFrontEndVal(
            STATE_LIST,
            (providerData as any)?.licensedStates
          ),
          specialities: transformMultiSelectFrontEndVal(
            DEFAULT_UUID_NAME_LIST,
            (providerData as any)?.specialities
          ),
        }}
      />
    </Box>
  );
};

export default AllUserTable;
